import { useEffect } from "react";
import { Button, ButtonProps, IconButton, useDisclosure } from "@chakra-ui/react";
import { getEventUID } from "applesauce-core/helpers";
import { getZapSender } from "applesauce-common/helpers";
import { useActiveAccount } from "applesauce-react/hooks";
import { getNutzapAmount, NUTZAP_INFO_KIND } from "applesauce-wallet/helpers";

import { hapticMedium } from "../../helpers/haptics";
import { humanReadableLits } from "../../helpers/lightning";
import { totalZaps } from "../../helpers/nostr/zaps";
import useEventZaps from "../../hooks/use-event-zaps";
import useEventNutzaps from "../../hooks/use-event-nutzaps";
import { NostrEvent } from "nostr-tools";
import { LightningIcon } from "../icons";
import ZapModal from "../event-zap-modal";
import useReplaceableEvent from "../../hooks/use-replaceable-event";
import { useReadRelays } from "../../hooks/use-client-relays";
import { zapsLoader, nutzapsLoader } from "../../services/loaders";

export type NoteZapButtonProps = Omit<ButtonProps, "children"> & {
  event: NostrEvent;
  allowComment?: boolean;
  showEventPreview?: boolean;
};

export default function EventZapButton({ event, allowComment, showEventPreview, ...props }: NoteZapButtonProps) {
  const account = useActiveAccount();
  const isSelf = !!account && account.pubkey === event.pubkey;
  const nutzapInfo = useReplaceableEvent({ kind: NUTZAP_INFO_KIND as number, pubkey: event.pubkey });
  const zaps = useEventZaps(event) ?? [];
  const nutzaps = useEventNutzaps(event);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const hasZapped = !!account && (zaps.some((zap) => getZapSender(zap) === account.pubkey) || nutzaps.some((n) => n.pubkey === account.pubkey));

  const readRelays = useReadRelays();

  useEffect(() => {
    const sub = nutzapsLoader(event, readRelays).subscribe();
    return () => sub.unsubscribe();
  }, [event.id]);
  const onZapped = () => {
    onClose();
    zapsLoader(event, readRelays).subscribe();
    nutzapsLoader(event, readRelays).subscribe();
  };

  const zapTotal = totalZaps(zaps);
  const nutzapTotal = nutzaps.reduce((sum, n) => sum + (getNutzapAmount(n) ?? 0), 0) * 1000;
  const total = zapTotal + nutzapTotal;

  return (
    <>
      {total > 0 ? (
        <Button
          leftIcon={<LightningIcon verticalAlign="sub" />}
          aria-label={isSelf ? "Tips received" : "Lit Note"}
          title={isSelf ? "Tips received" : "Lit Note"}
          colorScheme={hasZapped ? "primary" : undefined}
          {...props}
          onClick={isSelf ? undefined : () => { hapticMedium(); onOpen(); }}
          cursor={isSelf ? "default" : undefined}
          _hover={isSelf ? {} : undefined}
          _active={isSelf ? {} : undefined}
        >
          {humanReadableLits(total / 1000)}
        </Button>
      ) : (
        <IconButton
          icon={<LightningIcon verticalAlign="sub" />}
          aria-label={isSelf ? "Tips received" : "Lit Note"}
          title={isSelf ? "Tips received" : "Lit Note"}
          colorScheme={hasZapped ? "primary" : undefined}
          {...props}
          onClick={isSelf ? undefined : () => { hapticMedium(); onOpen(); }}
          cursor={isSelf ? "default" : undefined}
          _hover={isSelf ? {} : undefined}
          _active={isSelf ? {} : undefined}
        />
      )}

      {!isSelf && isOpen && (
        <ZapModal
          isOpen={isOpen}
          pubkey={event.pubkey}
          event={event}
          onClose={onClose}
          onZapped={onZapped}
          allowComment={allowComment}
          showEmbed={showEventPreview}
          nutzapInfo={nutzapInfo}
        />
      )}
    </>
  );
}
